// src/pages/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Trash2, 
  Edit2, 
  LayoutDashboard, 
  Briefcase, 
  FileText, 
  MessageSquare, 
  Plus, 
  Search, 
  LogOut, 
  Globe, 
  X, 
  ArrowRight,
  Sparkles,
  Layers,
  ChevronRight,
  BookOpen,
  Quote
} from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';

const API_BASE_URL = 'https://smartsolutionslk.com/api/api.php';
const UPLOADS_BASE_URL = 'https://smartsolutionslk.com/uploads/';

// Helper function to create a URL-friendly "slug"
const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^\w-]+/g, '');
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview'); // overview, projects, blogs, testimonials

  // State for search queries
  const [projectSearch, setProjectSearch] = useState('');
  const [projectCatFilter, setProjectCatFilter] = useState('All');
  const [blogSearch, setBlogSearch] = useState('');
  const [testimonialSearch, setTestimonialSearch] = useState('');

  // Workspaces Toggle State
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);

  // Project Fields
  const [projectTitle, setProjectTitle] = useState('');
  const [projectCategory, setProjectCategory] = useState('Web & App Engineering');
  const [projectImagePreview, setProjectImagePreview] = useState('');
  const [projectImageFile, setProjectImageFile] = useState(null);
  const [projectDescription, setProjectDescription] = useState('');
  const [projectDetails, setProjectDetails] = useState('');
  const [projectClientFeedback, setProjectClientFeedback] = useState('');
  const [projectLink, setProjectLink] = useState('');
  const [isProjectEditMode, setIsProjectEditMode] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectExistingImageUrl, setProjectExistingImageUrl] = useState('');

  // Blog Fields
  const [blogTitle, setBlogTitle] = useState('');
  const [blogAuthor, setBlogAuthor] = useState('');
  const [blogExcerpt, setBlogExcerpt] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogImagePreview, setBlogImagePreview] = useState('');
  const [blogImageFile, setBlogImageFile] = useState(null);
  const [isBlogEditMode, setIsBlogEditMode] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [blogExistingImageUrl, setBlogExistingImageUrl] = useState('');

  // Testimonial Fields
  const [testimonialQuote, setTestimonialQuote] = useState('');
  const [testimonialAuthor, setTestimonialAuthor] = useState('');
  const [testimonialCompany, setTestimonialCompany] = useState('');
  const [isTestimonialEditMode, setIsTestimonialEditMode] = useState(false);
  const [editingTestimonialId, setEditingTestimonialId] = useState(null);

  // Content Lists
  const [projects, setProjects] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  
  const [statusMessage, setStatusMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Fetch all data
  useEffect(() => {
    fetchData('projects', setProjects);
    fetchData('blogs', setBlogs);
    fetchData('testimonials', setTestimonials);
  }, []);

  const fetchData = async (resource, setter) => {
    try {
      const response = await fetch(`${API_BASE_URL}?resource=${resource}`);
      const data = await response.json();
      if (response.ok) {
        setter(data);
      } else {
        throw new Error(data.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error(`Error fetching ${resource}:`, error);
      setStatusMessage(`Error: Could not fetch ${resource}.`);
    }
  };

  // Image upload handler
  const uploadImage = async (file) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      setIsUploading(true);
      const response = await fetch(`${API_BASE_URL}?action=upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to upload image');
      return data.filename;
    } catch (error) {
      console.error('Error uploading image:', error);
      setStatusMessage(`Error uploading image: ${error.message}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Handle image file selection
  const handleImageChange = (e, setPreview, setFile) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddItem = async (resource, body, callback) => {
    try {
      const response = await fetch(`${API_BASE_URL}?resource=${resource}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'An error occurred.');
      
      setStatusMessage(`${resource.slice(0, -1)} added successfully!`);
      callback();
    } catch (error) {
      setStatusMessage(`Error adding ${resource.slice(0, -1)}: ${error.message}`);
    }
  };

  const handleDeleteItem = async (resource, id, callback) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const response = await fetch(`${API_BASE_URL}?resource=${resource}&id=${id}`, {
          method: 'DELETE',
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'An error occurred.');

        setStatusMessage('Item deleted successfully.');
        callback();
      } catch (error) {
        setStatusMessage(`Error deleting item: ${error.message}`);
      }
    }
  };

  // --- Project Edit Helpers ---
  const handleEditProjectClick = (project) => {
    setIsProjectEditMode(true);
    setEditingProjectId(project.id);
    setProjectTitle(project.title || '');
    setProjectCategory(project.category || 'Web & App Engineering');
    setProjectDescription(project.description || '');
    setProjectDetails(project.details || '');
    setProjectClientFeedback(project.client_feedback || '');
    setProjectLink(project.project_link || '');
    setProjectExistingImageUrl(project.image_url || '');
    setProjectImagePreview(project.image_url ? (project.image_url.startsWith('http') || project.image_url.startsWith('data:image') ? project.image_url : `${UPLOADS_BASE_URL}${project.image_url}`) : '');
    setProjectImageFile(null);
    setShowProjectForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelProjectEdit = () => {
    setIsProjectEditMode(false);
    setEditingProjectId(null);
    setProjectTitle('');
    setProjectCategory('Web & App Engineering');
    setProjectDescription('');
    setProjectDetails('');
    setProjectClientFeedback('');
    setProjectLink('');
    setProjectExistingImageUrl('');
    setProjectImagePreview('');
    setProjectImageFile(null);
    setShowProjectForm(false);
  };

  // --- Blog Edit Helpers ---
  const handleEditBlogClick = (blog) => {
    setIsBlogEditMode(true);
    setEditingBlogId(blog.id);
    setBlogTitle(blog.title || '');
    setBlogAuthor(blog.author || '');
    setBlogExcerpt(blog.excerpt || '');
    setBlogContent(blog.content || '');
    setBlogExistingImageUrl(blog.image_url || '');
    setBlogImagePreview(blog.image_url ? (blog.image_url.startsWith('http') || blog.image_url.startsWith('data:image') ? blog.image_url : `${UPLOADS_BASE_URL}${blog.image_url}`) : '');
    setBlogImageFile(null);
    setShowBlogForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelBlogEdit = () => {
    setIsBlogEditMode(false);
    setEditingBlogId(null);
    setBlogTitle('');
    setBlogAuthor('');
    setBlogExcerpt('');
    setBlogContent('');
    setBlogExistingImageUrl('');
    setBlogImagePreview('');
    setBlogImageFile(null);
    setShowBlogForm(false);
  };

  // --- Testimonial Edit Helpers ---
  const handleEditTestimonialClick = (testimonial) => {
    setIsTestimonialEditMode(true);
    setEditingTestimonialId(testimonial.id);
    setTestimonialQuote(testimonial.quote || '');
    setTestimonialAuthor(testimonial.author || '');
    setTestimonialCompany(testimonial.company || '');
    setShowTestimonialForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelTestimonialEdit = () => {
    setIsTestimonialEditMode(false);
    setEditingTestimonialId(null);
    setTestimonialQuote('');
    setTestimonialAuthor('');
    setTestimonialCompany('');
    setShowTestimonialForm(false);
  };

  // --- Form submission handlers ---
  const handleAddProject = async (e) => {
    e.preventDefault();
    
    let imageUrl = projectExistingImageUrl;
    if (projectImageFile) {
      const uploadedFilename = await uploadImage(projectImageFile);
      if (!uploadedFilename) {
        setStatusMessage('Failed to upload image. Please try again.');
        return;
      }
      imageUrl = uploadedFilename;
    }
    
    const body = { 
      title: projectTitle, 
      category: projectCategory,
      image_url: imageUrl,
      description: projectDescription,
      details: projectDetails,
      client_feedback: projectClientFeedback,
      project_link: projectLink
    };
    
    if (isProjectEditMode) {
      try {
        const response = await fetch(`${API_BASE_URL}?resource=projects&id=${editingProjectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'An error occurred.');
        
        setStatusMessage('Project updated successfully!');
        fetchData('projects', setProjects);
        handleCancelProjectEdit();
      } catch (error) {
        setStatusMessage(`Error updating project: ${error.message}`);
      }
    } else {
      handleAddItem('projects', body, () => {
        fetchData('projects', setProjects);
        handleCancelProjectEdit();
      });
    }
  };

  const handleAddBlog = async (e) => {
    e.preventDefault();
    
    let imageUrl = blogExistingImageUrl;
    if (blogImageFile) {
      const uploadedFilename = await uploadImage(blogImageFile);
      if (!uploadedFilename) {
        setStatusMessage('Failed to upload image. Please try again.');
        return;
      }
      imageUrl = uploadedFilename;
    }
    
    const body = {
      slug: createSlug(blogTitle),
      title: blogTitle,
      author: blogAuthor,
      date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      image_url: imageUrl,
      excerpt: blogExcerpt,
      content: blogContent
    };
    
    if (isBlogEditMode) {
      try {
        const response = await fetch(`${API_BASE_URL}?resource=blogs&id=${editingBlogId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'An error occurred.');
        
        setStatusMessage('Blog post updated successfully!');
        fetchData('blogs', setBlogs);
        handleCancelBlogEdit();
      } catch (error) {
        setStatusMessage(`Error updating blog post: ${error.message}`);
      }
    } else {
      handleAddItem('blogs', body, () => {
        fetchData('blogs', setBlogs);
        handleCancelBlogEdit();
      });
    }
  };
  
  const handleAddTestimonial = async (e) => {
    e.preventDefault();
    const body = { 
      quote: testimonialQuote, 
      author: testimonialAuthor,
      company: testimonialCompany 
    };

    if (isTestimonialEditMode) {
      try {
        const response = await fetch(`${API_BASE_URL}?resource=testimonials&id=${editingTestimonialId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'An error occurred.');
        
        setStatusMessage('Testimonial updated successfully!');
        fetchData('testimonials', setTestimonials);
        handleCancelTestimonialEdit();
      } catch (error) {
        setStatusMessage(`Error updating testimonial: ${error.message}`);
      }
    } else {
      handleAddItem('testimonials', body, () => {
        fetchData('testimonials', setTestimonials);
        handleCancelTestimonialEdit();
      });
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('isAdminAuthenticated');
    navigate('/');
  };

  // Helper for image URLs
  const getFullImgUrl = (url) => {
    if (!url) return 'https://placehold.co/100x100/1f2937/FFFFFF?text=No+Photo';
    if (url.startsWith('http') || url.startsWith('data:image')) return url;
    return `${UPLOADS_BASE_URL}${url}`;
  };

  // Filters and searches
  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(projectSearch.toLowerCase()) || 
                          (p.description && p.description.toLowerCase().includes(projectSearch.toLowerCase()));
    const matchesCategory = projectCatFilter === 'All' || p.category === projectCatFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredBlogs = blogs.filter(b => 
    b.title.toLowerCase().includes(blogSearch.toLowerCase()) || 
    b.author.toLowerCase().includes(blogSearch.toLowerCase())
  );

  const filteredTestimonials = testimonials.filter(t => 
    t.author.toLowerCase().includes(testimonialSearch.toLowerCase()) || 
    t.company.toLowerCase().includes(testimonialSearch.toLowerCase()) ||
    t.quote.toLowerCase().includes(testimonialSearch.toLowerCase())
  );

  return (
    <div className="bg-[#0b0f19] min-h-screen text-slate-100 flex flex-col md:flex-row antialiased">
      {/* Sidebar Navigation */}
      <aside className="w-full md:w-64 bg-[#0f172a]/95 border-b md:border-b-0 md:border-r border-slate-800 flex flex-col justify-between shrink-0 z-20 backdrop-blur-md">
        <div>
          {/* Logo Brand */}
          <div className="p-6 border-b border-slate-800/60 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center font-bold text-slate-900 text-lg shadow-lg shadow-cyan-500/20">
              S
            </div>
            <div>
              <h1 className="font-extrabold text-sm tracking-wide bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent uppercase">Smart Solutions</h1>
              <p className="text-[10px] text-cyan-500/80 font-bold uppercase tracking-widest">Core Console</p>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="p-4 space-y-1">
            <button
              onClick={() => { setActiveTab('overview'); handleCancelProjectEdit(); handleCancelBlogEdit(); handleCancelTestimonialEdit(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm font-semibold ${
                activeTab === 'overview' 
                  ? 'bg-cyan-500/10 text-cyan-400 shadow-inner' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <LayoutDashboard size={18} />
              Dashboard Overview
            </button>
            <button
              onClick={() => { setActiveTab('projects'); handleCancelBlogEdit(); handleCancelTestimonialEdit(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm font-semibold ${
                activeTab === 'projects' 
                  ? 'bg-cyan-500/10 text-cyan-400 shadow-inner' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <Briefcase size={18} />
              Project Portfolio
            </button>
            <button
              onClick={() => { setActiveTab('blogs'); handleCancelProjectEdit(); handleCancelTestimonialEdit(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm font-semibold ${
                activeTab === 'blogs' 
                  ? 'bg-cyan-500/10 text-cyan-400 shadow-inner' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <FileText size={18} />
              Blog Publisher
            </button>
            <button
              onClick={() => { setActiveTab('testimonials'); handleCancelProjectEdit(); handleCancelBlogEdit(); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-sm font-semibold ${
                activeTab === 'testimonials' 
                  ? 'bg-cyan-500/10 text-cyan-400 shadow-inner' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <MessageSquare size={18} />
              Client Testimonials
            </button>
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-slate-800/60 space-y-2">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:text-slate-200 text-xs font-semibold hover:bg-slate-800/40 transition-colors"
          >
            <Globe size={15} />
            View Public Site
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-400 hover:text-white hover:bg-red-500/10 text-xs font-semibold transition-colors"
          >
            <LogOut size={15} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Workspace Container */}
      <main className="flex-grow p-6 md:p-10 overflow-y-auto max-w-7xl mx-auto w-full">
        {statusMessage && (
          <div className="bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 p-4 rounded-xl mb-6 flex justify-between items-center animate-fade-in">
            <span className="text-sm font-medium">{statusMessage}</span>
            <button onClick={() => setStatusMessage('')} className="text-cyan-400 hover:text-white"><X size={16} /></button>
          </div>
        )}

        {/* Dashboard Tabs Content */}
        <AnimatePresence mode="wait">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
              className="space-y-8"
            >
              {/* Welcome Banner */}
              <div className="p-6 md:p-8 rounded-2xl bg-gradient-to-r from-slate-900 via-[#111a2e] to-[#0f172a] border border-slate-800 relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl" />
                <div className="relative z-10 space-y-2">
                  <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-bold uppercase tracking-wider">Administrator</span>
                  <h2 className="text-3xl font-extrabold text-white">Smart Solutions Core Console</h2>
                  <p className="text-slate-400 text-sm max-w-2xl">
                    Manage your client case studies, publish industry insights in the blog, and update client trust testimonials using rich text, layouts, and floating image editors.
                  </p>
                </div>
              </div>

              {/* Counts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Projects Stats */}
                <div 
                  onClick={() => setActiveTab('projects')}
                  className="p-6 rounded-2xl bg-gradient-to-br from-[#0e7490]/10 to-[#0b0f19] border border-cyan-500/25 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer shadow-xl group hover:scale-[1.02]"
                >
                  <div className="flex justify-between items-start">
                    <div className="p-3 bg-cyan-500/10 text-cyan-400 rounded-xl group-hover:bg-cyan-500 group-hover:text-black transition-all">
                      <Briefcase size={20} />
                    </div>
                    <span className="text-xs text-cyan-500 font-bold uppercase tracking-wider flex items-center gap-1">
                      Manage <ArrowRight size={10} />
                    </span>
                  </div>
                  <h3 className="text-4xl font-extrabold text-white mt-6">{projects.length}</h3>
                  <p className="text-slate-400 text-xs mt-1 font-semibold uppercase tracking-wider">Total Projects</p>
                </div>

                {/* Blogs Stats */}
                <div 
                  onClick={() => setActiveTab('blogs')}
                  className="p-6 rounded-2xl bg-gradient-to-br from-[#4d7c0f]/10 to-[#0b0f19] border border-lime-500/20 hover:border-lime-500/40 transition-all duration-300 cursor-pointer shadow-xl group hover:scale-[1.02]"
                >
                  <div className="flex justify-between items-start">
                    <div className="p-3 bg-lime-500/10 text-lime-400 rounded-xl group-hover:bg-lime-500 group-hover:text-black transition-all">
                      <FileText size={20} />
                    </div>
                    <span className="text-xs text-lime-400 font-bold uppercase tracking-wider flex items-center gap-1">
                      Manage <ArrowRight size={10} />
                    </span>
                  </div>
                  <h3 className="text-4xl font-extrabold text-white mt-6">{blogs.length}</h3>
                  <p className="text-slate-400 text-xs mt-1 font-semibold uppercase tracking-wider">Published Articles</p>
                </div>

                {/* Testimonials Stats */}
                <div 
                  onClick={() => setActiveTab('testimonials')}
                  className="p-6 rounded-2xl bg-gradient-to-br from-[#6d28d9]/10 to-[#0b0f19] border border-purple-500/25 hover:border-purple-500/50 transition-all duration-300 cursor-pointer shadow-xl group hover:scale-[1.02]"
                >
                  <div className="flex justify-between items-start">
                    <div className="p-3 bg-purple-500/10 text-purple-400 rounded-xl group-hover:bg-purple-500 group-hover:text-white transition-all">
                      <MessageSquare size={20} />
                    </div>
                    <span className="text-xs text-purple-400 font-bold uppercase tracking-wider flex items-center gap-1">
                      Manage <ArrowRight size={10} />
                    </span>
                  </div>
                  <h3 className="text-4xl font-extrabold text-white mt-6">{testimonials.length}</h3>
                  <p className="text-slate-400 text-xs mt-1 font-semibold uppercase tracking-wider">Client Reviews</p>
                </div>
              </div>

              {/* Quick Actions Shortcuts */}
              <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-2xl space-y-4 shadow-xl">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Sparkles className="text-cyan-400" size={18} />
                  Quick Actions Workspace
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button 
                    onClick={() => { setActiveTab('projects'); setShowProjectForm(true); setIsProjectEditMode(false); }}
                    className="flex items-center justify-between p-4 bg-slate-800/40 border border-slate-700/60 rounded-xl hover:bg-slate-850 hover:border-cyan-500/40 text-left text-sm font-semibold transition-all"
                  >
                    <span>+ Add New Project</span>
                    <ChevronRight size={16} className="text-slate-500" />
                  </button>
                  <button 
                    onClick={() => { setActiveTab('blogs'); setShowBlogForm(true); setIsBlogEditMode(false); }}
                    className="flex items-center justify-between p-4 bg-slate-800/40 border border-slate-700/60 rounded-xl hover:bg-slate-850 hover:border-lime-500/40 text-left text-sm font-semibold transition-all"
                  >
                    <span>+ Write Blog Post</span>
                    <ChevronRight size={16} className="text-slate-500" />
                  </button>
                  <button 
                    onClick={() => { setActiveTab('testimonials'); setShowTestimonialForm(true); setIsTestimonialEditMode(false); }}
                    className="flex items-center justify-between p-4 bg-slate-800/40 border border-slate-700/60 rounded-xl hover:bg-slate-850 hover:border-purple-500/40 text-left text-sm font-semibold transition-all"
                  >
                    <span>+ Add Client Review</span>
                    <ChevronRight size={16} className="text-slate-500" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* TAB 2: PROJECTS */}
          {activeTab === 'projects' && (
            <motion.div
              key="projects"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
              className="space-y-6"
            >
              {/* Header section */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800 pb-5">
                <div>
                  <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
                    <Briefcase className="text-cyan-400" />
                    Project Portfolio
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">Add, update, and manage clients case studies & services outcomes.</p>
                </div>
                {!showProjectForm && (
                  <button
                    onClick={() => { setShowProjectForm(true); setIsProjectEditMode(false); }}
                    className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold px-4 py-2.5 rounded-xl transition-all shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/25"
                  >
                    <Plus size={16} />
                    New Project
                  </button>
                )}
              </div>

              {/* Workspace form (drawer styling) */}
              {showProjectForm ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.99 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#0f172a] border border-slate-800 p-6 md:p-8 rounded-2xl space-y-6 shadow-2xl relative"
                >
                  <div className="flex justify-between items-center border-b border-slate-800 pb-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <Layers size={18} className="text-cyan-400" />
                      {isProjectEditMode ? `Edit Project: ${projectTitle}` : 'Create New Project'}
                    </h3>
                    <button 
                      onClick={handleCancelProjectEdit}
                      className="p-2 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <form onSubmit={handleAddProject} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="projectTitle" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Project Title</label>
                        <input 
                          type="text" 
                          id="projectTitle" 
                          value={projectTitle} 
                          onChange={(e) => setProjectTitle(e.target.value)} 
                          required 
                          placeholder="e.g. Finance Hub Web Platform"
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white placeholder-slate-600 transition-all" 
                        />
                      </div>
                      <div>
                        <label htmlFor="projectCategory" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Category</label>
                        <select 
                          id="projectCategory" 
                          value={projectCategory} 
                          onChange={(e) => setProjectCategory(e.target.value)} 
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white transition-all"
                        >
                          <option>Web & App Engineering</option>
                          <option>AI & Automation</option>
                          <option>E-commerce Platforms</option>
                          <option>Digital Marketing</option>
                          <option>Startup Acceleration</option>
                          <option>Smart Technologies</option> 
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Project Image (Main Photo)</label>
                        <div className="flex flex-col gap-3">
                          <input 
                            type="file" 
                            id="projectImage" 
                            onChange={(e) => handleImageChange(e, setProjectImagePreview, setProjectImageFile)} 
                            accept="image/*" 
                            className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-cyan-500/10 file:text-cyan-400 hover:file:bg-cyan-500/25 file:cursor-pointer transition-colors" 
                          />
                          <p className="text-[10px] text-slate-500">Max size 5MB. JPEG, PNG, GIF or WebP only.</p>
                        </div>
                      </div>
                      <div className="flex justify-center md:justify-start">
                        {projectImagePreview ? (
                          <div className="relative group rounded-xl overflow-hidden border border-slate-800 max-h-36 w-fit bg-slate-950">
                            <img src={projectImagePreview} alt="Project preview" className="max-h-32 object-cover object-center rounded-lg" />
                          </div>
                        ) : (
                          <div className="h-32 w-52 border border-dashed border-slate-800 rounded-xl flex items-center justify-center text-xs text-slate-600 bg-slate-950/40">
                            No main image selected
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="projectDescription" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Short Summary (Excerpt)</label>
                      <textarea 
                        id="projectDescription" 
                        value={projectDescription} 
                        onChange={(e) => setProjectDescription(e.target.value)} 
                        rows="2" 
                        placeholder="A brief overview of the project shown on listing pages..."
                        className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white placeholder-slate-600 transition-all"
                      />
                    </div>

                    {/* RichTextEditor Section */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Project Case Study / Full Details</label>
                        <span className="text-[10px] text-cyan-500 font-semibold bg-cyan-950/30 px-2 py-0.5 rounded-full border border-cyan-800/30">Rich Layout Supported</span>
                      </div>
                      <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950">
                        <RichTextEditor
                          value={projectDetails}
                          onChange={setProjectDetails}
                          placeholder="Write technical details, deliverables, results, or embed extra layout elements..."
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="projectClientFeedback" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Client Testimonial Quote</label>
                        <textarea 
                          id="projectClientFeedback" 
                          value={projectClientFeedback} 
                          onChange={(e) => setProjectClientFeedback(e.target.value)} 
                          rows="3" 
                          placeholder="What did the client say about Smart Solutions' results?..."
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white placeholder-slate-600 transition-all"
                        />
                      </div>
                      <div>
                        <label htmlFor="projectLink" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Live Website / Demo Link</label>
                        <input 
                          type="url" 
                          id="projectLink" 
                          value={projectLink} 
                          onChange={(e) => setProjectLink(e.target.value)} 
                          placeholder="https://example.com"
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white placeholder-slate-600 transition-all" 
                        />
                        <p className="text-[10px] text-slate-500 mt-2">Include protocol (e.g., https://).</p>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 border-t border-slate-800/60 pt-6">
                      <button 
                        type="button" 
                        onClick={handleCancelProjectEdit} 
                        className="px-5 py-2.5 bg-slate-850 hover:bg-slate-800 text-slate-300 font-semibold rounded-xl transition-all"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        disabled={isUploading} 
                        className="px-6 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/10"
                      >
                        {isUploading ? 'Uploading...' : (isProjectEditMode ? 'Save Changes' : 'Publish Project')}
                      </button>
                    </div>
                  </form>
                </motion.div>
              ) : (
                /* Search and Lists */
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3 bg-slate-900/30 p-4 border border-slate-800 rounded-2xl">
                    <div className="relative flex-grow">
                      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        placeholder="Search projects by title..."
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-950/60 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm placeholder-slate-600"
                      />
                    </div>
                    <select
                      value={projectCatFilter}
                      onChange={(e) => setProjectCatFilter(e.target.value)}
                      className="p-2.5 bg-slate-950/60 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm text-slate-300 transition-all shrink-0"
                    >
                      <option value="All">All Categories</option>
                      <option>Web & App Engineering</option>
                      <option>AI & Automation</option>
                      <option>E-commerce Platforms</option>
                      <option>Digital Marketing</option>
                      <option>Startup Acceleration</option>
                      <option>Smart Technologies</option>
                    </select>
                  </div>

                  <div className="bg-[#0f172a]/60 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm border-collapse">
                        <thead>
                          <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold text-xs uppercase tracking-wider">
                            <th className="p-4 pl-6">Preview</th>
                            <th className="p-4">Project Title</th>
                            <th className="p-4">Category</th>
                            <th className="p-4">Summary</th>
                            <th className="p-4 pr-6 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/40">
                          {filteredProjects.length > 0 ? filteredProjects.map(p => (
                            <tr key={p.id} className="hover:bg-slate-800/20 transition-colors group">
                              <td className="p-4 pl-6">
                                <img 
                                  src={getFullImgUrl(p.image_url)} 
                                  alt={p.title} 
                                  className="w-14 h-10 object-cover rounded-lg border border-slate-800"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://placehold.co/100x100/1f2937/FFFFFF?text=Error';
                                  }}
                                />
                              </td>
                              <td className="p-4 font-bold text-white max-w-xs truncate">{p.title}</td>
                              <td className="p-4 text-xs font-semibold text-cyan-400">
                                <span className="bg-cyan-500/10 border border-cyan-500/20 px-2 py-0.5 rounded-full">
                                  {p.category}
                                </span>
                              </td>
                              <td className="p-4 text-slate-400 text-xs max-w-sm truncate">{p.description || 'No description provided.'}</td>
                              <td className="p-4 pr-6 text-right">
                                <div className="flex justify-end gap-2">
                                  <button 
                                    onClick={() => handleEditProjectClick(p)} 
                                    className="p-2 text-cyan-400 hover:text-white bg-cyan-950/20 hover:bg-cyan-500 rounded-lg transition-all"
                                    title="Edit Project"
                                  >
                                    <Edit2 size={15} />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteItem('projects', p.id, () => fetchData('projects', setProjects))} 
                                    className="p-2 text-red-400 hover:text-white bg-red-950/20 hover:bg-red-600 rounded-lg transition-all"
                                    title="Delete Project"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="5" className="p-8 text-center text-slate-500 font-semibold italic">No projects found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* TAB 3: BLOGS */}
          {activeTab === 'blogs' && (
            <motion.div
              key="blogs"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
              className="space-y-6"
            >
              {/* Header section */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800 pb-5">
                <div>
                  <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
                    <FileText className="text-lime-400" />
                    Blog Publisher
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">Publish news, knowledge base items, updates, and corporate logs.</p>
                </div>
                {!showBlogForm && (
                  <button
                    onClick={() => { setShowBlogForm(true); setIsBlogEditMode(false); }}
                    className="flex items-center gap-2 bg-lime-500 hover:bg-lime-400 text-slate-900 font-bold px-4 py-2.5 rounded-xl transition-all shadow-lg shadow-lime-500/10 hover:shadow-lime-500/25"
                  >
                    <Plus size={16} />
                    New Blog Post
                  </button>
                )}
              </div>

              {/* Workspace form (drawer styling) */}
              {showBlogForm ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.99 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#0f172a] border border-slate-800 p-6 md:p-8 rounded-2xl space-y-6 shadow-2xl relative"
                >
                  <div className="flex justify-between items-center border-b border-slate-800 pb-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <BookOpen size={18} className="text-lime-400" />
                      {isBlogEditMode ? `Edit Blog Post: ${blogTitle}` : 'Write New Blog Post'}
                    </h3>
                    <button 
                      onClick={handleCancelBlogEdit}
                      className="p-2 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <form onSubmit={handleAddBlog} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="blogTitle" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Blog Title</label>
                        <input 
                          type="text" 
                          id="blogTitle" 
                          value={blogTitle} 
                          onChange={(e) => setBlogTitle(e.target.value)} 
                          required 
                          placeholder="e.g. Navigating AI Integrations in 2026"
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500 text-white placeholder-slate-600 transition-all" 
                        />
                      </div>
                      <div>
                        <label htmlFor="blogAuthor" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Author Name</label>
                        <input 
                          type="text" 
                          id="blogAuthor" 
                          value={blogAuthor} 
                          onChange={(e) => setBlogAuthor(e.target.value)} 
                          required 
                          placeholder="e.g. John Doe / Tech Team"
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500 text-white placeholder-slate-600 transition-all" 
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Blog Cover Image</label>
                        <div className="flex flex-col gap-3">
                          <input 
                            type="file" 
                            id="blogImage" 
                            onChange={(e) => handleImageChange(e, setBlogImagePreview, setBlogImageFile)} 
                            accept="image/*" 
                            className="w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-lime-500/10 file:text-lime-400 hover:file:bg-lime-500/25 file:cursor-pointer transition-colors" 
                          />
                          <p className="text-[10px] text-slate-500">Max size 5MB. JPEG, PNG, GIF or WebP.</p>
                        </div>
                      </div>
                      <div className="flex justify-center md:justify-start">
                        {blogImagePreview ? (
                          <div className="relative group rounded-xl overflow-hidden border border-slate-800 max-h-36 w-fit bg-slate-950">
                            <img src={blogImagePreview} alt="Blog preview" className="max-h-32 object-cover object-center rounded-lg" />
                          </div>
                        ) : (
                          <div className="h-32 w-52 border border-dashed border-slate-800 rounded-xl flex items-center justify-center text-xs text-slate-600 bg-slate-950/40">
                            No cover image selected
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="blogExcerpt" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Short Summary (Excerpt)</label>
                      <textarea 
                        id="blogExcerpt" 
                        value={blogExcerpt} 
                        onChange={(e) => setBlogExcerpt(e.target.value)} 
                        rows="2" 
                        required
                        placeholder="A brief excerpt explaining what this article covers..."
                        className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500 text-white placeholder-slate-600 transition-all"
                      />
                    </div>

                    {/* RichTextEditor Section */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Article Content</label>
                        <span className="text-[10px] text-lime-400 font-semibold bg-lime-950/30 px-2 py-0.5 rounded-full border border-lime-800/30">Rich Layout & Image Sizing Enabled</span>
                      </div>
                      <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950">
                        <RichTextEditor
                          value={blogContent}
                          onChange={setBlogContent}
                          placeholder="Write the full content of your post. Use the alignment tools and image settings (click on images inside editor) to design layouts..."
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 border-t border-slate-800/60 pt-6">
                      <button 
                        type="button" 
                        onClick={handleCancelBlogEdit} 
                        className="px-5 py-2.5 bg-slate-850 hover:bg-slate-800 text-slate-300 font-semibold rounded-xl transition-all"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        disabled={isUploading} 
                        className="px-6 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-900 font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-lime-500/10"
                      >
                        {isUploading ? 'Uploading...' : (isBlogEditMode ? 'Save Changes' : 'Publish Article')}
                      </button>
                    </div>
                  </form>
                </motion.div>
              ) : (
                /* Search and Lists */
                <div className="space-y-4">
                  <div className="bg-slate-900/30 p-4 border border-slate-800 rounded-2xl">
                    <div className="relative w-full">
                      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        placeholder="Search blog articles by title or author..."
                        value={blogSearch}
                        onChange={(e) => setBlogSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-950/60 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500 text-sm placeholder-slate-600"
                      />
                    </div>
                  </div>

                  <div className="bg-[#0f172a]/60 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm border-collapse">
                        <thead>
                          <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold text-xs uppercase tracking-wider">
                            <th className="p-4 pl-6">Cover</th>
                            <th className="p-4">Article Title</th>
                            <th className="p-4">Author</th>
                            <th className="p-4">Published Date</th>
                            <th className="p-4 pr-6 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/40">
                          {filteredBlogs.length > 0 ? filteredBlogs.map(b => (
                            <tr key={b.id} className="hover:bg-slate-800/20 transition-colors">
                              <td className="p-4 pl-6">
                                <img 
                                  src={getFullImgUrl(b.image_url)} 
                                  alt={b.title} 
                                  className="w-14 h-10 object-cover rounded-lg border border-slate-800"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://placehold.co/100x100/1f2937/FFFFFF?text=Error';
                                  }}
                                />
                              </td>
                              <td className="p-4 font-bold text-white max-w-xs truncate">{b.title}</td>
                              <td className="p-4 text-slate-300 text-xs">{b.author || 'N/A'}</td>
                              <td className="p-4 text-slate-400 text-xs">{b.date}</td>
                              <td className="p-4 pr-6 text-right">
                                <div className="flex justify-end gap-2">
                                  <button 
                                    onClick={() => handleEditBlogClick(b)} 
                                    className="p-2 text-cyan-400 hover:text-white bg-cyan-950/20 hover:bg-cyan-500 rounded-lg transition-all"
                                    title="Edit Article"
                                  >
                                    <Edit2 size={15} />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteItem('blogs', b.id, () => fetchData('blogs', setBlogs))} 
                                    className="p-2 text-red-400 hover:text-white bg-red-950/20 hover:bg-red-600 rounded-lg transition-all"
                                    title="Delete Article"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="5" className="p-8 text-center text-slate-500 font-semibold italic">No articles found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* TAB 4: TESTIMONIALS */}
          {activeTab === 'testimonials' && (
            <motion.div
              key="testimonials"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
              className="space-y-6"
            >
              {/* Header section */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800 pb-5">
                <div>
                  <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
                    <MessageSquare className="text-purple-400" />
                    Client Testimonials
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">Manage corporate feedback, reviews, and client quotes.</p>
                </div>
                {!showTestimonialForm && (
                  <button
                    onClick={() => { setShowTestimonialForm(true); setIsTestimonialEditMode(false); }}
                    className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-bold px-4 py-2.5 rounded-xl transition-all shadow-lg shadow-purple-600/10 hover:shadow-purple-600/25"
                  >
                    <Plus size={16} />
                    New Testimonial
                  </button>
                )}
              </div>

              {/* Workspace form (drawer styling) */}
              {showTestimonialForm ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.99 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#0f172a] border border-slate-800 p-6 md:p-8 rounded-2xl space-y-6 shadow-2xl relative"
                >
                  <div className="flex justify-between items-center border-b border-slate-800 pb-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <Quote size={18} className="text-purple-400" />
                      {isTestimonialEditMode ? `Edit Testimonial` : 'Add New Testimonial'}
                    </h3>
                    <button 
                      onClick={handleCancelTestimonialEdit}
                      className="p-2 text-slate-400 hover:text-white bg-slate-800/60 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <form onSubmit={handleAddTestimonial} className="space-y-6">
                    <div>
                      <label htmlFor="testimonialQuote" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Client Testimonial Quote</label>
                      <textarea 
                        id="testimonialQuote" 
                        value={testimonialQuote} 
                        onChange={(e) => setTestimonialQuote(e.target.value)} 
                        rows="4" 
                        required
                        placeholder="Paste client feedback quote here..."
                        className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-slate-600 transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="testimonialAuthor" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Author Name</label>
                        <input 
                          type="text" 
                          id="testimonialAuthor" 
                          value={testimonialAuthor} 
                          onChange={(e) => setTestimonialAuthor(e.target.value)} 
                          required 
                          placeholder="e.g. Sarah Jenkins"
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-slate-600 transition-all" 
                        />
                      </div>
                      <div>
                        <label htmlFor="testimonialCompany" className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Designation / Company</label>
                        <input 
                          type="text" 
                          id="testimonialCompany" 
                          value={testimonialCompany} 
                          onChange={(e) => setTestimonialCompany(e.target.value)} 
                          required 
                          placeholder="e.g. Chief Operations Officer, Vertex Corp"
                          className="w-full p-3 bg-slate-950/80 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-slate-600 transition-all" 
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 border-t border-slate-800/60 pt-6">
                      <button 
                        type="button" 
                        onClick={handleCancelTestimonialEdit} 
                        className="px-5 py-2.5 bg-slate-850 hover:bg-slate-800 text-slate-300 font-semibold rounded-xl transition-all"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-purple-600/10"
                      >
                        {isTestimonialEditMode ? 'Save Changes' : 'Publish Testimonial'}
                      </button>
                    </div>
                  </form>
                </motion.div>
              ) : (
                /* Search and Lists */
                <div className="space-y-4">
                  <div className="bg-slate-900/30 p-4 border border-slate-800 rounded-2xl">
                    <div className="relative w-full">
                      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input
                        type="text"
                        placeholder="Search testimonials by author, company, or text..."
                        value={testimonialSearch}
                        onChange={(e) => setTestimonialSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-950/60 border border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm placeholder-slate-600"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredTestimonials.length > 0 ? filteredTestimonials.map(t => (
                      <div 
                        key={t.id} 
                        className="bg-[#0f172a]/60 border border-slate-800/80 hover:border-slate-800 p-6 rounded-2xl shadow-xl flex flex-col justify-between relative overflow-hidden group hover:scale-[1.01] transition-all duration-200"
                      >
                        <div className="absolute top-2 right-2 flex gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => handleEditTestimonialClick(t)} 
                            className="p-1.5 text-cyan-400 hover:text-white bg-slate-900/80 rounded-lg transition-colors"
                            title="Edit Testimonial"
                          >
                            <Edit2 size={13} />
                          </button>
                          <button 
                            onClick={() => handleDeleteItem('testimonials', t.id, () => fetchData('testimonials', setTestimonials))} 
                            className="p-1.5 text-red-400 hover:text-white bg-slate-900/80 rounded-lg transition-colors"
                            title="Delete Testimonial"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <div className="text-slate-300 italic text-sm leading-relaxed mb-6 font-medium pr-10">
                          "{t.quote}"
                        </div>
                        <div className="border-t border-slate-800/60 pt-4 flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-500/10 text-purple-400 font-extrabold flex items-center justify-center text-xs border border-purple-500/20">
                            {t.author ? t.author[0] : '"'}
                          </div>
                          <div>
                            <div className="text-xs font-bold text-white">{t.author}</div>
                            <div className="text-[10px] text-slate-500 font-semibold">{t.company}</div>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="md:col-span-2 p-8 text-center text-slate-500 font-semibold italic border border-slate-800 rounded-2xl bg-slate-950/20">
                        No testimonials found.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default AdminPanel;
